import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './Header';
import { Footer } from './Footer';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { House, Cat, Package, ChatCircle, ShoppingBag, ArrowLeft, ArrowRight, List, X, Bell } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';
import { LanguageToggle } from '../ui/LanguageToggle';
import { ThemeToggle } from '../ui/ThemeToggle';
import { CatLoading } from '../ui/CatLoading';
import { PullToRefresh } from './PullToRefresh';
import { adminApi } from '../../api/client';
import { REFRESH_EVENT } from '../../hooks/useListRefresh';

export const Layout: React.FC = () => {
  return (
    <div className="flex flex-col min-h-[100dvh]">
      <Header />
      <main className="flex-1 pt-16 lg:pt-18" id="main-content">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export const AdminLayout: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t, isRTL } = useI18n();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-base-50 dark:bg-base-950">
        <CatLoading size="md" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-base-50 dark:bg-base-950">
        <div className="text-center space-y-4">
          <h1 className="text-heading-lg font-bold text-base-900 dark:text-base-100">{t('admin.accessDenied')}</h1>
          <p className="text-base-600 dark:text-base-400">{t('admin.accessDeniedDesc')}</p>
          <Link to="/login" className="btn-primary inline-flex">
            {t('admin.signIn')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] bg-base-50 dark:bg-base-950">
      {/* Mobile backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-base-900/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 z-50 w-64 bg-white dark:bg-base-900 dark:border-base-800 transform transition-transform duration-150 lg:translate-x-0',
          isRTL ? 'right-0 border-l border-base-200' : 'left-0 border-r border-base-200',
          sidebarOpen
            ? 'translate-x-0'
            : isRTL
              ? 'translate-x-full lg:translate-x-0'
              : '-translate-x-full lg:translate-x-0'
        )}
        aria-label="Admin sidebar"
      >
        <AdminSidebar onClose={() => setSidebarOpen(false)} />
      </aside>

      <div className={cn('flex-1 min-w-0', isRTL ? 'lg:mr-64' : 'lg:ml-64')}>
        <header className="sticky top-0 z-30 bg-white/95 dark:bg-base-900/95 backdrop-blur-xl border-b border-base-200 dark:border-base-800">
          <AdminHeader onMenuToggle={() => setSidebarOpen(true)} />
        </header>
        <main className="p-4 lg:p-8" id="main-content">
          <PullToRefresh>
            <Outlet />
          </PullToRefresh>
        </main>
      </div>
    </div>
  );
};

interface AdminSidebarProps {
  onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const { t, isRTL } = useI18n();

  const navigation = [
    { path: '/admin', label: t('nav.admin.dashboard'), icon: House },
    { path: '/admin/cats', label: t('nav.admin.cats'), icon: Cat },
    { path: '/admin/products', label: t('nav.admin.products'), icon: Package },
    { path: '/admin/adoption-requests', label: t('admin.adoptionRequests'), icon: ChatCircle },
    { path: '/admin/purchase-requests', label: t('admin.purchaseRequests'), icon: ShoppingBag },
  ];

  return (
    <div className="flex flex-col h-full p-4 lg:p-6">
      <div className="flex items-center justify-between mb-8 lg:mb-10">
        <span className="text-heading-lg font-bold text-base-900 dark:text-base-100">{t('nav.admin')}</span>
        <button
          onClick={onClose}
          className="p-2 rounded-xl text-base-500 hover:bg-base-100 dark:hover:bg-base-800 lg:hidden transition-all"
          aria-label="Close sidebar"
        >
          <X size={20} weight="bold" />
        </button>
      </div>
      <nav className="flex-1 space-y-1" aria-label="Admin navigation">
        {navigation.map((item) => {
          const isActive = item.path === '/admin'
            ? location.pathname === '/admin'
            : location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-md font-medium transition-all duration-150',
                isActive
                  ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
                  : 'text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100'
              )}
            >
              <item.icon size={20} weight={isActive ? 'fill' : 'regular'} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="pt-4 border-t border-base-200 dark:border-base-700">
        {import.meta.env.VITE_APP_MODE === 'admin' ? (
          <button
            onClick={onClose}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-body-md font-medium text-base-400 dark:text-base-500 cursor-default"
            tabIndex={-1}
          >
            <ArrowLeft size={20} weight="regular" className={isRTL ? 'rotate-180' : ''} />
            {t('nav.admin')}
          </button>
        ) : (
          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-md font-medium text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 transition-all duration-150"
          >
            <ArrowLeft size={20} weight="regular" className={isRTL ? 'rotate-180' : ''} />
            {t('nav.backToStore')}
          </Link>
        )}
      </div>
    </div>
  );
};

interface AdminHeaderProps {
  onMenuToggle: () => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ onMenuToggle }) => {
  const { t } = useI18n();
  return (
    <div className="container-page h-16 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-xl text-base-500 hover:bg-base-100 dark:hover:bg-base-800 lg:hidden transition-all"
          aria-label="Open menu"
        >
          <List size={24} weight="bold" />
        </button>
        <h1 className="text-heading-lg font-bold text-base-900 dark:text-base-100">{t('admin.dashboard')}</h1>
      </div>
      <div className="flex items-center gap-2">
        <NotificationBell />
        <LanguageToggle />
        <ThemeToggle />
        <span className="text-body-sm text-base-500 dark:text-base-400 hidden sm:inline">{t('nav.admin')}</span>
      </div>
    </div>
  );
};

const NotificationBell: React.FC = () => {
  const { t } = useI18n();
  const [pending, setPending] = useState({ purchase: 0, adoption: 0 });
  const [open, setOpen] = useState(false);
  const prevTotalRef = useRef(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await adminApi.getStats();
        if (!active) return;
        const s = res.data.data;
        const next = { purchase: s.pendingPurchaseRequests ?? 0, adoption: s.pendingAdoptionRequests ?? 0 };
        const nextTotal = next.purchase + next.adoption;
        if (prevTotalRef.current > 0 && nextTotal > prevTotalRef.current) {
          // New pending request detected -> auto-refresh admin lists.
          window.dispatchEvent(new CustomEvent(REFRESH_EVENT, { detail: { source: 'bell-poll' } }));
        }
        prevTotalRef.current = nextTotal;
        setPending(next);
      } catch (err) {
        console.warn('[bell] stats failed', err);
      }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const total = pending.purchase + pending.adoption;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl text-base-500 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 transition-all"
        aria-label={t('nav.admin')}
        aria-expanded={open}
      >
        <Bell size={22} weight={total > 0 ? 'fill' : 'regular'} />
        {total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-base-900">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className={cn(
                'absolute z-50 mt-2 right-0 w-72 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-base-200 dark:border-base-700 bg-white dark:bg-base-900 shadow-xl overflow-hidden'
              )}
            >
              <div className="px-4 py-3 border-b border-base-200 dark:border-base-700">
                <p className="text-body-sm font-semibold text-base-900 dark:text-base-100">{t('admin.pendingRequests')}</p>
              </div>
              <div className="p-2 space-y-1">
                <Link
                  to="/admin/purchase-requests"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-base-100 dark:hover:bg-base-800 transition-all"
                >
                  <span className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                      <ShoppingBag size={20} weight="fill" />
                    </span>
                    <span className="text-body-sm font-medium text-base-900 dark:text-base-100">{t('admin.purchaseRequests')}</span>
                  </span>
                  <span className={cn('text-body-sm font-bold', pending.purchase > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-base-400')}>
                    {pending.purchase}
                  </span>
                </Link>
                <Link
                  to="/admin/adoption-requests"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-base-100 dark:hover:bg-base-800 transition-all"
                >
                  <span className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400">
                      <ChatCircle size={20} weight="fill" />
                    </span>
                    <span className="text-body-sm font-medium text-base-900 dark:text-base-100">{t('admin.adoptionRequests')}</span>
                  </span>
                  <span className={cn('text-body-sm font-bold', pending.adoption > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-base-400')}>
                    {pending.adoption}
                  </span>
                </Link>
                {total === 0 && (
                  <p className="px-3 py-4 text-center text-body-sm text-base-400">{t('admin.noPendingRequests')}</p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
